import { useEffect } from 'react';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ContentCard from '@v2/shared/components/ContentCard';
import { trackEvent } from '@v2/shared/lib/analytics';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import Band from './Band';
import StillNeedHelp from './StillNeedHelp';
import { CARD_GAP, H2 } from './styles';
import { HelpCenterSearchProps } from './types';

export default function HelpSearch({ props }: { props: HelpCenterSearchProps }) {
  const { buildUrl } = useUrlGenerator();

  useEffect(() => {
    if (props.query === '') {
      return;
    }

    trackEvent('help_search', { query: props.query, results: props.count });

    if (props.count === 0) {
      trackEvent('help_search_no_results', { query: props.query });
    }
  }, [props.query, props.count]);

  return (
    <>
      <Breadcrumbs items={props.breadcrumbs} />

      <Band
        title={props.query === '' ? 'Search help' : `Results for “${props.query}”`}
        intro={
          props.query === ''
            ? undefined
            : `${props.count} article${props.count === 1 ? '' : 's'} matched.`
        }
        searchAction={props.searchAction}
        kustomer={props.kustomer}
        categoryTitles={props.categoryTitles}
        query={props.query}
      />

      <ContentCard className={CARD_GAP}>
        {props.count === 0 ? (
          <>
            <h2 className={H2}>No articles matched</h2>
            <p className="m-0 text-slate-500">
              Try a different word, or{' '}
              <a href={buildUrl('/help')} className="font-semibold text-primary no-underline hover:underline">
                browse every topic
              </a>
              . If the answer is not here, our team can help.
            </p>
          </>
        ) : (
          <ul className="m-0 list-none p-0">
            {props.results.map((result) => (
              <li
                key={result.slug}
                className="m-0 border-0 border-t border-solid border-slate-200 py-4 first:border-t-0 first:pt-0"
              >
                <a
                  href={buildUrl(result.href)}
                  className="text-[17px] font-bold text-slate-900 no-underline hover:text-primary"
                >
                  {result.title}
                </a>
                {result.summary ? <p className="mb-0 mt-1 text-sm text-slate-500">{result.summary}</p> : null}
                {result.category ? (
                  <p className="mb-0 mt-1 text-xs text-slate-500">
                    In{' '}
                    <a
                      href={buildUrl(result.categoryHref)}
                      className="font-semibold text-primary no-underline hover:underline"
                    >
                      {result.category}
                    </a>
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </ContentCard>

      <StillNeedHelp />
    </>
  );
}
